// Library Methods
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Auth0Provider } from "@auth0/auth0-react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// Components
import App from "./App";
import Loader from "./components/shared/loaders/Loader/Loader";
import Box from "@mui/material/Box";

// Providers
import ThemeProvider from "./contexts/theme/ThemeProvider";
import ThemeModeProvider from "./contexts/theme/ThemeModeContext";
import ClinicProvider from "./contexts/clinic/ClinicProvider";
import DevicesProvider from "./contexts/devices/DevicesContext";
import SnackbarProvider from "./contexts/alerts/SnackbarContext";
import ExamTransferProvider from "./contexts/exams/ExamTransferContext";
import LiveUpdateContextProvider from "./contexts/live-update/LiveUpdateContext";

// Configs
import reportWebVitals from "./config/reportWebVitals";
import "./config/i18n";

// Styles
import "./styles/tailwind/output.css";
import { PageContainerWrapper } from "./styles/mui/muiStylesHelper";

// Utils
import { isProduction } from "./utils/env/envHelper";

const queryClient = new QueryClient();

// Replace console.* for disabling log on production
if (isProduction) {
  console.log = () => {};
  console.error = () => {};
  console.warn = () => {};
  console.debug = () => {};
}

// Do not show "ResizeObserver loop completed with undelivered notifications"
// It is a benign browser message, not a bug/error
window.addEventListener("error", (event) => {
  if (
    event.message ===
    "ResizeObserver loop completed with undelivered notifications."
  ) {
    event.stopImmediatePropagation();
  }
});

const domain = process.env.REACT_APP_AUTH0_DOMAIN ?? "";
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID ?? "";
const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
const scope = process.env.REACT_APP_AUTH0_SCOPE;
const redirectUri = `${process.env.REACT_APP_URL}/callback`;

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience,
        scope,
      }}
    >
      <BrowserRouter>
        <SnackbarProvider>
          <QueryClientProvider client={queryClient}>
            <ClinicProvider>
              <DevicesProvider>
                <LiveUpdateContextProvider>
                  <ExamTransferProvider>
                    {/* <ReactQueryDevtools initialIsOpen /> */}
                    <ThemeModeProvider>
                      <Suspense
                        fallback={
                          <Box sx={{ ...PageContainerWrapper(), padding: 0 }}>
                            <Loader />
                          </Box>
                        }
                      >
                        <ThemeProvider>
                          <App />
                        </ThemeProvider>
                      </Suspense>
                    </ThemeModeProvider>
                  </ExamTransferProvider>
                </LiveUpdateContextProvider>
              </DevicesProvider>
            </ClinicProvider>
          </QueryClientProvider>
        </SnackbarProvider>
      </BrowserRouter>
    </Auth0Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
